/**
 * Customer Document Persistence API
 * 
 * Client-side utilities for persisting and retrieving customer documents
 * from the D1 database via Cloudflare Pages Functions.
 */

import { buildStorySnapshot, type StorySnapshot } from './storySnapshot';
import { validateJsonResponse } from './responseValidation';
import { runDecisionEngine } from '../api/decisionEngine';
import { assertValidDocId, extractDocId, isValidDocId } from './docId';
import { getEnvironment, getSessionId } from './debug/debugBundleHelpers';
import { trackLifecycleEvent } from '../mapping';
import { ATLAS_DATA_CONTRACT_VERSION, migrateDoc } from '../contracts/dataContract';

const getAppVersion = (): string => {
  try {
    // @ts-ignore - build-time injected
    return import.meta.env?.VITE_APP_VERSION ||
      // @ts-ignore - git info
      import.meta.env?.VITE_GIT_SHA ||
      'dev';
  } catch {
    return 'dev';
  }
};

export type CreateDocPayload = {
  rawInput?: StorySnapshot['rawInput'];
  notes?: string;
  route?: string;
};

export interface PersistResponse {
  docId: string;
  url: string;
  expiresAt: string;
}

export interface DocumentError {
  ok?: boolean;
  code?: string;
  error?: string;
  message?: string;
  details?: any;
  docId?: string;
}

function sanitizeForPersistence(payload: StorySnapshot): StorySnapshot {
  const seen = new WeakSet<object>();

  const sanitizeValue = (value: any): any => {
    if (value === null) return null;

    const t = typeof value;
    if (t === 'undefined' || t === 'function' || t === 'symbol') return undefined;
    if (t === 'bigint') return String(value);
    if (t === 'number') return Number.isFinite(value) ? value : null;
    if (t === 'string' || t === 'boolean') return value;
    if (value instanceof Date) return value.toISOString();
    if (t !== 'object') return value;

    if (seen.has(value)) return undefined;
    seen.add(value);

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          const sanitized = sanitizeValue(item);
          return typeof sanitized === 'undefined' ? null : sanitized;
        });
    }

    return Object.entries(value).reduce<Record<string, any>>((acc, [key, nested]) => {
      const sanitized = sanitizeValue(nested);
      if (typeof sanitized !== 'undefined') {
        acc[key] = sanitized;
      }
      return acc;
    }, {});
  };

  return {
    version: payload.version,
    updatedAt: payload.updatedAt,
    meta: sanitizeValue(payload.meta),
    notes: payload.notes,
    rawInput: sanitizeValue(payload.rawInput) ?? {},
    // Engine output is derived from rawInput and should not block saves.
    engineOutput: null,
    selectedOptionId: payload.selectedOptionId ?? null,
    selectedOptionAddOns: sanitizeValue(payload.selectedOptionAddOns) ?? [],
    optionSlots: sanitizeValue(payload.optionSlots),
  };
}

function hydrateCurrentBoilerOutput(rawInput: any, engineInput: any) {
  if (!rawInput || typeof rawInput !== 'object') return rawInput;

  const currentBoilerKw = rawInput.currentBoilerKw;
  const hasCurrentBoilerKw = !!(currentBoilerKw && currentBoilerKw !== 'unknown');
  if (hasCurrentBoilerKw) return rawInput;

  const boilerOutputKw = engineInput?.currentSystemEfficiency?.boilerOutputKw;
  if (typeof boilerOutputKw !== 'number' || !Number.isFinite(boilerOutputKw)) {
    return rawInput;
  }

  const normalizedKw = Number.isInteger(boilerOutputKw)
    ? String(boilerOutputKw)
    : String(Number(boilerOutputKw.toFixed(1)));

  return {
    ...rawInput,
    currentBoilerKw: normalizedKw,
  };
}

/**
 * Stamp a retrieved snapshot with the current data contract version if it was
 * saved before the contract was introduced, and run the migration pipeline.
 *
 * This is a no-op when contractVersion is already present.
 */
function applyDocMigration(snapshot: StorySnapshot): StorySnapshot {
  if (snapshot.contractVersion) {
    return snapshot;
  }
  const toVersion = ATLAS_DATA_CONTRACT_VERSION;
  const migrated = migrateDoc({ ...snapshot } as Record<string, unknown>, 'pre-contract', toVersion) as unknown as StorySnapshot;
  return {
    ...migrated,
    contractVersion: toVersion,
    migratedFromLegacy: true,
  };
}

export async function createDocument({
  rawInput = {} as StorySnapshot['rawInput'],
  notes = '',
  route,
}: CreateDocPayload = {}): Promise<PersistResponse> {
  const resolvedRoute = route ?? (typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}`
    : 'unknown');

  trackLifecycleEvent('mapping_initialized', 'documentPersistence.ts:createDocument', {
    keys: Object.keys(rawInput).length,
    note: 'Creating new document',
  });

  const snapshot: StorySnapshot = buildStorySnapshot({
    meta: {
      createdAt: new Date().toISOString(),
      route: resolvedRoute,
      appVersion: getAppVersion(),
      env: getEnvironment(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sessionId: getSessionId(),
    },
    notes,
    rawInput,
    contractVersion: ATLAS_DATA_CONTRACT_VERSION,
    engineOutput: null,
    selectedOptionId: null,
  });

  return persistDocument(snapshot);
}

/**
 * Update an existing customer document in D1.
 * Keeps the same doc ID while refreshing payload_json.
 *
 * @throws Error if update fails or returns invalid data
 */
export async function updateDocument(docId: string, payload: StorySnapshot): Promise<PersistResponse> {
  assertValidDocId(docId);

  trackLifecycleEvent('doc_save_requested', 'documentPersistence.ts:updateDocument', {
    docId,
    keys: payload?.rawInput ? Object.keys(payload.rawInput).length : 0,
    note: 'Updating existing document',
  });

  if (!payload) {
    console.error('[client] CRITICAL: Attempted to update null/undefined payload');
    throw new Error('Cannot update document: payload is null or undefined');
  }

  if (typeof payload !== 'object') {
    console.error('[client] CRITICAL: Payload is not an object', { payloadType: typeof payload });
    throw new Error('Cannot update document: payload must be an object');
  }

  if (!payload.version || !payload.updatedAt) {
    console.error('[client] CRITICAL: Payload missing required fields', {
      hasVersion: !!payload.version,
      hasUpdatedAt: !!payload.updatedAt,
      keys: Object.keys(payload),
    });
    throw new Error('Cannot update document: payload missing required fields (version, updatedAt)');
  }

  const persistablePayload = sanitizeForPersistence(payload);

  let requestBody: string;
  try {
    const s = JSON.stringify({ payload: persistablePayload });
    if (typeof s !== 'string') {
      console.error('[client] CRITICAL: JSON.stringify returned non-string', {
        result: s,
        resultType: typeof s,
        payloadKeys: Object.keys(payload),
      });
      throw new Error('Failed to serialize payload to JSON');
    }
    requestBody = s;
  } catch (error) {
    console.error('[client] CRITICAL: JSON serialization failed', {
      error: error instanceof Error ? error.message : String(error),
    });
    throw new Error(`Cannot update document: ${error instanceof Error ? error.message : 'payload serialization failed'}`);
  }

  const response = await fetch(`/api/story/update/${docId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: requestBody,
  });

  await validateJsonResponse(response);

  if (!response.ok) {
    const error: DocumentError = await response.json().catch(() => ({
      ok: false,
      code: 'UNKNOWN_ERROR',
      error: 'Failed to update document',
      message: `HTTP ${response.status}`,
    }));

    const err = new Error(error.message || error.error || 'Failed to update document') as any;
    err.code = error.code;
    err.details = error.details;
    err.status = response.status;
    err.endpoint = `/api/story/update/${docId}`;
    throw err;
  }

  const result = await response.json();

  if (!result.docId || !result.url) {
    console.error('[client] CRITICAL: Update response missing required fields', { result });
    throw new Error('Invalid response from update API - missing docId or url');
  }

  trackLifecycleEvent('doc_save_completed', 'documentPersistence.ts:updateDocument', {
    docId: result.docId,
    note: 'Document updated successfully',
  });

  return result;
}

/**
 * Persist a customer document to D1.
 * Returns doc ID and URL for QR code generation.
 * 
 * Every call creates a new document — never updates existing ones.
 * 
 * @throws Error if persistence fails or returns invalid data
 */
export async function persistDocument(payload: StorySnapshot): Promise<PersistResponse> {
  trackLifecycleEvent('doc_save_requested', 'documentPersistence.ts:persistDocument', {
    keys: payload?.rawInput ? Object.keys(payload.rawInput).length : 0,
    note: 'Persisting new document',
  });

  // Guardrail: Validate payload before sending to API
  if (!payload) {
    console.error('[client] CRITICAL: Attempted to persist null/undefined payload');
    throw new Error('Cannot persist document: payload is null or undefined');
  }

  // Guardrail: Validate payload structure
  if (typeof payload !== 'object') {
    console.error('[client] CRITICAL: Payload is not an object', { payloadType: typeof payload });
    throw new Error('Cannot persist document: payload must be an object');
  }

  // Guardrail: Validate required fields exist
  if (!payload.version || !payload.updatedAt) {
    console.error('[client] CRITICAL: Payload missing required fields', { 
      hasVersion: !!payload.version,
      hasUpdatedAt: !!payload.updatedAt,
      keys: Object.keys(payload)
    });
    throw new Error('Cannot persist document: payload missing required fields (version, updatedAt)');
  }

  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const persistablePayload = sanitizeForPersistence(payload);
  
  if (isDev) {
    console.log('[client] Persisting document:', { 
      timestamp: new Date().toISOString(),
      payloadKeys: Object.keys(payload),
      version: persistablePayload.version,
      updatedAt: persistablePayload.updatedAt
    });
  }
  
  // Guardrail: Test JSON serialization before sending to catch errors early
  let requestBody: string;
  try {
    const s = JSON.stringify({ payload: persistablePayload });
    
    // Critical: JSON.stringify can return undefined for certain inputs
    // (e.g., functions, symbols at top level, or if payload itself is undefined)
    if (typeof s !== "string") {
      console.error('[client] CRITICAL: JSON.stringify returned non-string (likely undefined)', {
        result: s,
        resultType: typeof s,
        payloadType: typeof payload,
        payloadKeys: Object.keys(payload)
      });
      throw new Error('Failed to serialize payload to JSON - returned non-string (likely undefined)');
    }
    
    requestBody = s;
    
    // Optional: warn (not fail) on empty payload to catch minimal docs
    if (requestBody === '{"payload":{}}') {
      console.warn('[client] WARNING: Payload serialized to empty object - this is valid but may indicate missing data', {
        payloadKeys: Object.keys(payload)
      });
    }
  } catch (error) {
    // Catch JSON.stringify errors (circular references, BigInt, etc.)
    console.error('[client] CRITICAL: JSON serialization failed', { 
      error: error instanceof Error ? error.message : String(error),
      payloadType: typeof payload,
      payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : null
    });
    throw new Error(`Cannot persist document: ${error instanceof Error ? error.message : 'payload serialization failed'}`);
  }
  
  const response = await fetch('/api/story/persist', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: requestBody,
  });

  // Validate Content-Type
  await validateJsonResponse(response);

  // Now we know it's JSON, safe to parse
  if (!response.ok) {
    const error: DocumentError = await response.json().catch(() => ({
      ok: false,
      code: 'UNKNOWN_ERROR',
      error: 'Failed to persist document',
      message: `HTTP ${response.status}`,
    }));
    
    if (isDev) {
      console.error('[client] Persist failed:', {
        status: response.status,
        code: error.code,
        error: error.error || error.message,
        message: error.message,
        details: error.details
      });
    }
    
    // Throw with full error details for UI to display
    const err = new Error(error.message || error.error || 'Failed to persist document') as any;
    err.code = error.code;
    err.details = error.details;
    err.status = response.status;
    err.endpoint = '/api/story/persist';
    throw err;
  }

  const result = await response.json();
  
  // Guardrail: Validate response contains required fields
  if (!result.docId || !result.url) {
    console.error('[client] CRITICAL: Persist response missing required fields', { result });
    throw new Error('Invalid response from persist API - missing docId or url');
  }
  
  const resolvedDocId = isValidDocId(result.docId)
    ? result.docId
    : extractDocId(result.url);

  // Guardrail: Validate docId format
  if (!resolvedDocId) {
    console.error('[client] CRITICAL: Persist returned invalid docId format', { docId: result.docId, url: result.url });
    throw new Error('Invalid docId format returned from persist API');
  }

  if (resolvedDocId !== result.docId) {
    console.warn('[client] WARNING: Persist response docId mismatch, using extracted docId from URL', {
      docId: result.docId,
      resolvedDocId,
      url: result.url,
    });
  }

  if (isDev) {
    console.log('[client] Document persisted successfully:', { docId: resolvedDocId });
  }

  trackLifecycleEvent('doc_save_completed', 'documentPersistence.ts:persistDocument', {
    docId: resolvedDocId,
    note: 'Document persisted successfully',
  });

  return { ...result, docId: resolvedDocId };
}

/**
 * Retrieve a persisted document from D1.
 * 
 * @throws Error if document not found (404) or expired (410)
 */
export async function retrieveDocument(docId: string): Promise<StorySnapshot> {
  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  trackLifecycleEvent('api_fetch_started', 'documentPersistence.ts:retrieveDocument', {
    docId,
    note: 'Fetching document from API',
  });

  if (isDev) {
    console.log('[client] Retrieving document:', { docId, endpoint: `/api/story/doc/${docId}` });
  }
  
  const response = await fetch(`/api/story/doc/${docId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (isDev) {
    console.log('[client] Document retrieval response:', { 
      docId, 
      status: response.status, 
      ok: response.ok,
      statusText: response.statusText
    });
  }

  // Validate Content-Type
  await validateJsonResponse(response);

  // Now we know it's JSON, safe to parse
  if (!response.ok) {
    const error: DocumentError = await response.json().catch(() => ({
      error: 'Failed to retrieve document',
      message: `HTTP ${response.status}`,
    }));

    if (isDev) {
      console.error('[client] Document retrieval failed:', { 
        docId, 
        status: response.status, 
        errorCode: error.code,
        error: error.error,
        message: error.message
      });
    }

    if (response.status === 404) {
      throw new Error('Document not found');
    }
    if (response.status === 410) {
      throw new Error('Document expired');
    }

    throw new Error(error.message || error.error);
  }

  if (isDev) {
    console.log('[client] Document retrieved successfully:', { docId });
  }
  const payload = await response.json();

  trackLifecycleEvent('api_fetch_completed', 'documentPersistence.ts:retrieveDocument', {
    docId,
    note: 'Document retrieved successfully',
  });

  if (payload && typeof payload === 'object' && ('rawInput' in payload || 'engineOutput' in payload)) {
    if (isDev) {
      console.log('[client] Document payload resolved from snapshot shape', { docId });
    }

    if (!payload.engineOutput && payload.rawInput) {
      try {
        if (isDev) {
          console.log('[client] Rehydrating legacy document locally', { docId });
        }
        const result = await runDecisionEngine(payload.rawInput);
        
        trackLifecycleEvent('hydration_completed', 'documentPersistence.ts:retrieveDocument', {
          docId,
          keys: Object.keys(payload.rawInput).length,
          note: 'Legacy document rehydrated',
        });
        
        return applyDocMigration({
          ...payload,
          rawInput: hydrateCurrentBoilerOutput(payload.rawInput, result.normalizedInput),
          engineOutput: {
            input: result.normalizedInput,
            output: result.output,
            normalizedOutput: result.normalizedOutput,
          },
        } as StorySnapshot);
      } catch (error) {
        console.error('[client] Failed to rehydrate legacy document', {
          docId,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    trackLifecycleEvent('doc_loaded', 'documentPersistence.ts:retrieveDocument', {
      docId,
      keys: payload.rawInput ? Object.keys(payload.rawInput).length : 0,
      note: 'Document loaded from snapshot',
    });

    return applyDocMigration({
      ...payload,
      rawInput: hydrateCurrentBoilerOutput(payload.rawInput, payload.engineOutput?.input),
    } as StorySnapshot);
  }

  const recommendationFromPayload = payload?.recommendation
    ?? payload?.data?.recommendation
    ?? payload?.output
    ?? null;

  const recommendationSource = payload?.recommendation
    ? 'payload.recommendation'
    : payload?.data?.recommendation
      ? 'payload.data.recommendation'
      : payload?.output
        ? 'payload.output'
        : 'none';

  if (isDev) {
    console.log('[client] Document payload normalized from legacy shape', {
      docId,
      recommendationSource,
    });
  }

  const engineInput = payload?.engineOutput?.input
    ?? payload?.data?.engineOutput?.input
    ?? payload?.input
    ?? payload?.data?.input
    ?? payload?.engineInput
    ?? payload?.data?.engineInput
    ?? {};

  const snapshot = {
    version: payload?.version ?? '1.0.0',
    updatedAt: payload?.updatedAt ?? new Date().toISOString(),
    rawInput: hydrateCurrentBoilerOutput(
      payload?.rawInput
        ?? payload?.data?.rawInput
        ?? payload?.input
        ?? payload?.data?.input
        ?? {},
      engineInput
    ),
    engineOutput: payload?.engineOutput
      ?? payload?.data?.engineOutput
      ?? (recommendationFromPayload
        ? {
          input: engineInput,
          output: recommendationFromPayload,
        }
        : null),
    selectedOptionId: payload?.selectedOptionId
      ?? payload?.data?.selectedOptionId
      ?? payload?.recommendation?.selectedOptionId
      ?? null,
    selectedOptionAddOns: payload?.selectedOptionAddOns
      ?? payload?.data?.selectedOptionAddOns
      ?? [],
  } as StorySnapshot;

  if (!snapshot.engineOutput && snapshot.rawInput) {
    try {
      if (isDev) {
        console.log('[client] Rehydrating normalized legacy document locally', { docId });
      }
      const result = await runDecisionEngine(snapshot.rawInput);
      
      trackLifecycleEvent('hydration_completed', 'documentPersistence.ts:retrieveDocument', {
        docId,
        keys: Object.keys(snapshot.rawInput).length,
        note: 'Normalized legacy document rehydrated',
      });
      
      return applyDocMigration({
        ...snapshot,
        rawInput: hydrateCurrentBoilerOutput(snapshot.rawInput, result.normalizedInput),
        engineOutput: {
          input: result.normalizedInput,
          output: result.output,
          normalizedOutput: result.normalizedOutput,
        },
      });
    } catch (error) {
      console.error('[client] Failed to rehydrate normalized legacy document', {
        docId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  trackLifecycleEvent('doc_loaded', 'documentPersistence.ts:retrieveDocument', {
    docId,
    keys: snapshot.rawInput ? Object.keys(snapshot.rawInput).length : 0,
    note: 'Legacy document normalized and loaded',
  });

  return applyDocMigration(snapshot);
}

/**
 * Check if a document exists and is not expired.
 * Returns true if document is accessible, false otherwise.
 */
export async function checkDocument(docId: string): Promise<boolean> {
  try {
    await retrieveDocument(docId);
    return true;
  } catch {
    return false;
  }
}
